// Der Datenvertrag der Besichtigung (E2): Was die Doku-App Stück für Stück
// an den Server schickt – ein Eingang für alle Einträge (POST
// /api/doku/eintrag), geprüft auf beiden Seiten mit denselben Regeln.
//
// Das ist das teuer zu ändernde Stück der Etappe (Plan E2, Entscheidung 3):
// Aussehen und Abläufe lassen sich jederzeit billig ändern, wie Kunde,
// Station und Foto gespeichert werden und zusammenhängen, nicht. Deshalb
// steht hier schon jede Art, auch die, die der Server erst in einem späteren
// Schritt annimmt.
//
// Grundregeln:
//  - Jede Kennung entsteht am Gerät (UUID) – auch ohne Empfang hat alles
//    schon seinen festen Namen.
//  - Jeder Eintrag trägt seine Aufnahme und seine Kennung; der Server nimmt
//    ihn beliebig oft an, er ist danach genau einmal da.
//  - `kennung` ist die Kennung des Datensatzes, den der Eintrag betrifft:
//    bei „aufnahme“ und „abgeschlossen“ die Aufnahme, bei „k2“ und
//    „montagetermin“ die Anlage, bei „nachtrag“ und „nachtrag-abgeschlossen“
//    der Nachtrag, bei „frage“ und „frage-stand“ die Frage, bei „antwort“ die
//    Antwort.
//  - `nachtragId` tragen alle Einträge, die im Zuge von „Etwas nachreichen“
//    entstehen (E2, Schritt 5) – außer denen, die jederzeit kommen dürfen
//    und nie zu einem Nachtrag gehören: der Dachplanung mit K2 und seit E3
//    den Fragen, Antworten, „erledigt“ und dem Montagetermin.
//
// Seit E3 (Schritt 1, Teil 2): Fragen mit Zusammenhang (Grundsatz 9) und der
// Montagetermin (Gernots Entscheidung 4) kommen aus der Doku-App über
// denselben Eingang, auch ohne Netz über den Postausgang. Ihre Felder sind
// die des Büro-Vertrags (buero-vertrag.ts) – eine Regel, zwei Türen.

import * as z from "zod";
import { dachFaktenVertrag, type DachFakten } from "./dach-fakten.ts";
import { LAENGEN_SCHLUESSEL, STATIONEN, STATION_SCHLUESSEL } from "./daten.ts";
import { freiwillig, kennung, kundeFelder, namensFelder, objektFelder, pflicht, zeitpunkt } from "./vertrag-bausteine.ts";
import {
  antwortFelder,
  FRAGE_AN_DER_BESICHTIGUNG,
  FRAGE_STAENDE,
  frageAnDerBesichtigung,
  frageFelder,
  materialFelder,
  montageterminFelder,
  type FrageAmServer,
} from "./buero-vertrag.ts";

/** Die Grenzen des Vertrags – an einer Stelle, damit App und Server dieselben kennen. */
export const AUFNAHME_GRENZEN = {
  /** Notizen, „Sonstiges“, Anmerkungen */
  text: 5000,
  meter: 9999,
  dauerSekunden: 3600,
  striche: 200,
  punkte: 20_000,
  /** Kennungen je Liste beim Abschluss */
  kennungenJeListe: 2000,
} as const;

export const ZEICHEN_WERKZEUGE = ["stift", "kreis", "pfeil"] as const;
export type ZeichenWerkzeug = (typeof ZEICHEN_WERKZEUGE)[number];

/**
 * Wie kräftig ein Strich ist (seit 22.10.2026, O66; seit E3b die Wahl am
 * Gerät, Gernot 22.10.2026). Freiwillig: Ohne Angabe ist ein Strich
 * „mittel“, so bleibt jede ältere Zeichnung gültig. Die Breiten stehen in
 * zeichnung.ts. Die Werte bleiben für immer gültig – auch, wenn ein Knopf
 * wegfällt; sonst blieben ungesendete Fotos im Postausgang hängen
 * (Code-Prüfung 22.10.2026, Befund 1).
 */
export const STRICH_STAERKEN = ["duenn", "mittel", "dick"] as const;
export type StrichStaerke = (typeof STRICH_STAERKEN)[number];

/**
 * Die Farbe eines Strichs (seit E3b, O66 – drei Farben, die Claude gewählt
 * hat, O81 e). Freiwillig: Ohne Angabe ist ein Strich Lime, wie jede ältere
 * Zeichnung. Die Farbwerte stehen in zeichnung.ts – ins Bild kommt nie der
 * Text vom Gerät. Werte kommen nur dazu, nie weg.
 */
export const STRICH_FARBEN = ["lime", "tuerkis", "pink"] as const;
export type StrichFarbe = (typeof STRICH_FARBEN)[number];

/** Sprachmemos im Original: das iPhone liefert MP4 (AAC), andere Browser WebM. */
export const MEMO_FORMATE = ["audio/mp4", "audio/webm"] as const;
export type MemoFormat = (typeof MEMO_FORMATE)[number];

/** Der Excel-Report von K2 – nur .xlsx (Prüfung des Plans E3c, Befund 11: K2 kann auch .xls und .csv, angenommen wird nur das eine). */
export const XLSX = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

/**
 * Was der Datei-Eingang (PUT /api/doku/datei/:pruefsumme) annimmt – je Art
 * die größte Datei in Bytes. Fotos kommen verkleinert (2048 Pixel, meist
 * unter 2 MB), Memos im Original, das K2-PDF so, wie K2 es macht. Seit E3c
 * (S3) der Excel-Report von K2 – die Unterkonstruktionsliste, die mit der
 * Bestellung an den Lieferanten geht; der Inhalt wird geprüft (datei-inhalt.ts).
 */
export const DATEI_ARTEN = {
  "image/jpeg": 5 * 1024 * 1024,
  "audio/mp4": 20 * 1024 * 1024,
  "audio/webm": 20 * 1024 * 1024,
  "application/pdf": 25 * 1024 * 1024,
  [XLSX]: 10 * 1024 * 1024,
} as const;
export type DateiArt = keyof typeof DATEI_ARTEN;

/** Die Art einer Datei aus ihrem content-type („audio/webm;codecs=opus“ → „audio/webm“) – null, wenn der Eingang sie nicht annimmt. */
export function dateiArt(inhaltstyp: string | null | undefined): DateiArt | null {
  const art = (inhaltstyp ?? "").split(";")[0]!.trim().toLowerCase();
  return Object.hasOwn(DATEI_ARTEN, art) ? (art as DateiArt) : null;
}

/**
 * Was ein Eintrag „geloescht“ entfernen darf. Stationen, Kunde, Objekt und
 * Aufnahme nicht. Seit E3b dazu das Material von draußen (O65) – Werte kommen
 * nur dazu, nie weg (Code-Prüfung 22.10.2026, Befund 1).
 */
export const LOESCHBARE_TABELLEN = ["foto", "notiz", "sprachmemo", "laenge", "materialangabe"] as const;
export type LoeschbareTabelle = (typeof LOESCHBARE_TABELLEN)[number];

// --- Bausteine ----------------------------------------------------------------
// Kennung, Zeitpunkt, Name und Adresse stehen seit E3 in vertrag-bausteine.ts
// (dort steht, warum) – hier weitergereicht, damit alles, was sie von hier
// nimmt, weiter funktioniert.

export { freiwillig, kennung, kundeFelder, objektFelder, pflicht, zeitpunkt };

/** Die Prüfsumme einer Datei (SHA-256, Kleinbuchstaben) – sie ist zugleich ihr Name am Server. */
export const PRUEFSUMME_MUSTER = /^[0-9a-f]{64}$/;
const pruefsumme = z.string().regex(PRUEFSUMME_MUSTER, "Keine gültige Prüfsumme.");

const freiText = z.string().max(AUFNAHME_GRENZEN.text, `Höchstens ${AUFNAHME_GRENZEN.text} Zeichen.`);

// --- Die Zeichnung auf einem Foto ----------------------------------------------

/** Ein Punkt auf dem Foto, als Anteil von Breite und Höhe (0 bis 1) – unabhängig von der Bildgröße. */
const punkt = z.tuple([z.number().min(0).max(1), z.number().min(0).max(1)]);

const strich = z
  .object({
    werkzeug: z.enum(ZEICHEN_WERKZEUGE),
    /** Freiwillig (seit 22.10.2026): fehlt sie, ist der Strich „mittel“ – die Fassung bleibt 1. */
    staerke: z.enum(STRICH_STAERKEN).optional(),
    /** Freiwillig (seit E3b): fehlt sie, ist der Strich Lime – die Fassung bleibt 1. */
    farbe: z.enum(STRICH_FARBEN).optional(),
    punkte: z.array(punkt).min(1).max(AUFNAHME_GRENZEN.punkte),
  })
  // Kreis und Pfeil entstehen durch Ziehen: Anfang und Ende.
  .refine((s) => s.werkzeug === "stift" || s.punkte.length === 2, "Kreis und Pfeil haben genau zwei Punkte.");

/**
 * Die Zeichnung als Daten (Striche), nicht als Bild: Das Bild dazu erzeugt
 * der Kern, so kommt kein fremdes SVG ungeprüft ins Büro (Plan E2). „Zurück“
 * nimmt am Gerät den letzten Strich weg; gespeichert wird, was bleibt.
 */
export const zeichnung = z
  .object({
    /** Fassung des Formats – ändert es sich, bleibt alte Ware lesbar. */
    fassung: z.literal(1),
    striche: z.array(strich).max(AUFNAHME_GRENZEN.striche, `Höchstens ${AUFNAHME_GRENZEN.striche} Striche.`),
  })
  .refine(
    (zg) => zg.striche.reduce((summe, s) => summe + s.punkte.length, 0) <= AUFNAHME_GRENZEN.punkte,
    `Höchstens ${AUFNAHME_GRENZEN.punkte} Punkte je Zeichnung.`,
  );
export type Zeichnung = z.infer<typeof zeichnung>;

// --- Kennungslisten beim Abschluss ---------------------------------------------

const kennungsListe = z.array(kennung).max(AUFNAHME_GRENZEN.kennungenJeListe);

/**
 * Alle Kennungen einer Aufnahme (oder eines Nachtrags), je Tabelle – für den
 * Abgleich „nichts fehlt“.
 *
 * `materialangabe` kam mit E3b dazu (O65, Material draußen). Ein Gerät mit
 * altem Stand der App schickt die Liste nicht – sie gilt dann als leer: Es
 * kann kein Material angelegt haben. So geht sein Abschluss weiter durch.
 */
export const kennungsListen = z.object({
  station: kennungsListe,
  foto: kennungsListe,
  notiz: kennungsListe,
  sprachmemo: kennungsListe,
  laenge: kennungsListe,
  materialangabe: kennungsListe.default([]),
});
export type KennungsListen = z.infer<typeof kennungsListen>;
/** Die Kennungslisten, wie ein Gerät sie schickt – `materialangabe` darf fehlen (alter Stand der App). */
export type KennungsListenVomGeraet = z.input<typeof kennungsListen>;

/**
 * Die Tabellen des Abgleichs, in dieser Reihenfolge – EINE Liste für Server
 * (eintraege.ts) und Doku-App (postausgang.ts), damit keine Seite eine
 * vergisst (Prüfung des Plans E3b, Befund 3).
 */
export const ABGLEICH_TABELLEN = ["station", "foto", "notiz", "sprachmemo", "laenge", "materialangabe"] as const satisfies readonly (keyof KennungsListen)[];

/** Leere Kennungslisten – je Tabelle des Abgleichs eine. */
export function leereKennungsListen(): KennungsListen {
  return { station: [], foto: [], notiz: [], sprachmemo: [], laenge: [], materialangabe: [] };
}

// --- Die Einträge ----------------------------------------------------------------

/** Was jeder Eintrag trägt. */
const kopf = {
  aufnahmeId: kennung,
  kennung,
  /** Gehört der Eintrag zu „Etwas nachreichen“, steht hier der Nachtrag. */
  nachtragId: kennung.optional(),
};

const OHNE_NACHTRAG = { message: "Dieser Eintrag gehört nie zu einem Nachtrag.", path: ["nachtragId"] };

/** Neue Besichtigung: Kunde, Objekt, Anlage, Aufnahme und die sieben Stationen auf einmal. */
const aufnahmeEintrag = z
  .object({
    ...kopf,
    art: z.literal("aufnahme"),
    kunde: z.object({
      id: kennung,
      /** Person – Firma und Eigentümergemeinschaft sind vorbereitet, aber gesperrt. */
      art: z.literal("person"),
      ...kundeFelder,
      /** Seit E3b (O82): freiwillig – ein Gerät mit altem Stand schickt nur `name`. */
      ...namensFelder,
    }),
    objekt: z.object({
      id: kennung,
      /** Einfamilienhaus – die anderen Arten sind vorbereitet, aber gesperrt. */
      art: z.literal("einfamilienhaus"),
      ...objektFelder,
    }),
    anlage: z.object({
      id: kennung,
      /** In Stufe 1 nur Sun (daten/produktlinien.json). */
      produktlinie: z.literal("sun"),
    }),
    begonnenAm: zeitpunkt,
    stationen: z
      .array(z.object({ id: kennung, art: z.enum(STATION_SCHLUESSEL) }))
      .length(STATIONEN.length, `Genau ${STATIONEN.length} Stationen.`)
      .refine((liste) => new Set(liste.map((s) => s.art)).size === liste.length, "Jede Station genau einmal.")
      .refine((liste) => new Set(liste.map((s) => s.id)).size === liste.length, "Jede Station mit eigener Kennung."),
  })
  .refine((e) => e.kennung === e.aufnahmeId, { message: "Die Kennung ist die der Aufnahme.", path: ["kennung"] })
  .refine((e) => e.nachtragId === undefined, OHNE_NACHTRAG);

/**
 * Eine Station: „fertig“ und „Sonstiges“. Die Station selbst entsteht mit der Aufnahme.
 *
 * Seit E3c (S5; ANFORDERUNGEN.md H32): `fachfragen` – die Gegebenheiten am
 * Dach (dach-fakten.ts: Höhe bis zur Dachrinne, Zugang, ein Satz für die
 * Dachpartie), nur an der Station „dach“. Das Feld darf fehlen: Ein Gerät mit
 * altem Stand schickt es nicht, dann bleibt am Server, was dort steht. Leer
 * ({}) heißt „nichts angegeben“. Im Nachtrag lässt es sich berichtigen wie
 * „Sonstiges“ (Prüfung des Plans E3c, Befund 10).
 */
const stationEintrag = z.object({
  ...kopf,
  art: z.literal("station"),
  fertig: z.boolean(),
  sonstiges: freiText.nullable(),
  fachfragen: dachFaktenVertrag.optional(),
});

/** Die Notiz zur ganzen Station oder die Foto-Notiz – genau eines von beiden. */
const notizEintrag = z
  .object({
    ...kopf,
    art: z.literal("notiz"),
    stationId: kennung.nullable(),
    fotoId: kennung.nullable(),
    text: freiText,
  })
  .refine((e) => (e.stationId === null) !== (e.fotoId === null), "Eine Notiz gehört genau zu einer Station oder einem Foto.");

/** Eine Länge (Kabelkanal, Rohr, Kabel – daten/laengen-arten.json). */
const laengeEintrag = z.object({
  ...kopf,
  art: z.literal("laenge"),
  stationId: kennung,
  laengenArt: z.enum(LAENGEN_SCHLUESSEL),
  meter: z
    .number()
    .min(0, "Die Länge kann nicht negativ sein.")
    .max(AUFNAHME_GRENZEN.meter, `Höchstens ${AUFNAHME_GRENZEN.meter} Meter.`),
  anmerkung: freiText.nullable(),
});

/**
 * Material, das der Verkäufer draußen angibt (seit E3b, O65 – Manuels
 * Wunsch): auf der Seite „Material“ am Schluss (`stationId` null) oder über
 * den Knopf „Material“ in einer Station (dann deren Kennung). Aus der
 * Standardliste angetippt (`standardId`) oder „Etwas anderes“ (null).
 * Bezeichnung, Einheit und Artikelnummer gehen als Kopie mit – das Büro
 * entscheidet bei der Freigabe (Grundsatz 7). Kein Verweis auf eine achte
 * Station: Die Besichtigung hat weiter genau sieben.
 *
 * Im Nachtrag wie eine Länge: Neues kommt dazu, die Menge einer gesendeten
 * lässt sich berichtigen, löschen nicht (0 eintragen).
 */
const materialEintrag = z.object({
  ...kopf,
  art: z.literal("material"),
  stationId: kennung.nullable(),
  standardId: kennung.nullable(),
  ...materialFelder,
});

/**
 * Ein Foto. Die Datei kommt vorher über den Datei-Eingang (benannt nach der
 * Prüfsumme); Größe und Maße liest der Server selbst aus ihr.
 */
const fotoEintrag = z.object({
  ...kopf,
  art: z.literal("foto"),
  stationId: kennung,
  reihenfolge: z.int().min(0).max(9999),
  pruefsumme,
  /** Vor dem Verkleinern ausgelesen; null, wenn das Foto keine Zeit trug. */
  aufnahmezeit: zeitpunkt.nullable(),
  zeichnung: zeichnung.nullable(),
});

/** Ein Sprachmemo hängt an einer Notiz (zur Station oder zum Foto). */
const memoEintrag = z.object({
  ...kopf,
  art: z.literal("memo"),
  notizId: kennung,
  pruefsumme,
  format: z.enum(MEMO_FORMATE),
  dauerSekunden: z.number().min(0).max(AUFNAHME_GRENZEN.dauerSekunden, "Ein Memo dauert höchstens eine Stunde."),
  aufgenommenAm: zeitpunkt,
});

/**
 * „Dachplanung mit K2“: das PDF (Prüfsumme) oder null für „überspringen“.
 * Kennung: die Anlage. Nie Teil eines Nachtrags (seit E2, Schritt 5): Die
 * Dachplanung darf jederzeit kommen, auch nach dem Senden, und hängt an der
 * Anlage, nicht an einer Sendung.
 *
 * Seit E3c (S5; ANFORDERUNGEN.md H24): dazu der Excel-Report von K2
 * (`excel`, die Prüfsumme der .xlsx – die Liste für den Lieferanten). Beide
 * Felder dürfen fehlen, eines muss da sein; was fehlt, bleibt am Server, wie
 * es ist. Ein Gerät mit altem Stand schickt nur das PDF. Reicht das Gerät nur
 * die Liste nach, schickt es das PDF nicht noch einmal mit – sonst nähme es
 * womöglich eines zurück, das das Büro inzwischen angehängt hat. Die Liste
 * lässt sich ersetzen, nicht entfernen.
 */
const k2Eintrag = z
  .object({
    ...kopf,
    art: z.literal("k2"),
    pruefsumme: pruefsumme.nullable().optional(),
    excel: pruefsumme.optional(),
  })
  .refine((e) => e.pruefsumme !== undefined || e.excel !== undefined, "Zur Dachplanung fehlt das PDF, „überspringen“ oder der Excel-Report.")
  .refine((e) => e.nachtragId === undefined, OHNE_NACHTRAG);

/** Etwas löschen – die Kennung ist die des Gelöschten. */
const geloeschtEintrag = z.object({
  ...kopf,
  art: z.literal("geloescht"),
  tabelle: z.enum(LOESCHBARE_TABELLEN),
});

/** „An das Büro senden“: alle Kennungen der Aufnahme; der Server bestätigt erst, wenn alles da ist. */
const abgeschlossenEintrag = z
  .object({
    ...kopf,
    art: z.literal("abgeschlossen"),
    kennungen: kennungsListen,
  })
  .refine((e) => e.kennung === e.aufnahmeId, { message: "Die Kennung ist die der Aufnahme.", path: ["kennung"] })
  .refine((e) => e.nachtragId === undefined, OHNE_NACHTRAG);

const NACHTRAG_KENNUNG = { message: "Die Kennung ist die des Nachtrags.", path: ["nachtragId"] };

/** „Etwas nachreichen“ beginnt: der Nachtrag selbst. */
const nachtragEintrag = z
  .object({
    ...kopf,
    art: z.literal("nachtrag"),
    begonnenAm: zeitpunkt,
  })
  .refine((e) => e.nachtragId === e.kennung, NACHTRAG_KENNUNG);

/** „Nachtrag senden“: die Kennungen genau dieses Nachtrags, gleicher Abgleich wie beim ersten Abschluss. */
const nachtragAbgeschlossenEintrag = z
  .object({
    ...kopf,
    art: z.literal("nachtrag-abgeschlossen"),
    kennungen: kennungsListen,
  })
  .refine((e) => e.nachtragId === e.kennung, NACHTRAG_KENNUNG);

// --- Seit E3: Fragen, Antworten, „erledigt“, Montagetermin ----------------------------
// Alle vier dürfen jederzeit kommen – in jedem Stand der Besichtigung, auch
// nach der Freigabe – und gehören nie zu einem Nachtrag: Eine Frage ist kein
// Stück der Besichtigung, das gesendet und abgeglichen wird, sondern ein
// Gespräch an ihr (PLAN.md, E3, „Handwerk“, Fragen). `aufnahmeId` ist die
// Besichtigung, an der gefragt wird – der Server prüft, dass Frage und Bezug
// zu ihr gehören.

/** Eine Frage aus der Doku-App. Kennung: die Frage. Die Felder sind die von `frageEingabe` (ohne doppelte Kennung). */
const frageEintrag = z
  .object({
    ...kopf,
    art: z.literal("frage"),
    ...frageFelder,
  })
  .refine(frageAnDerBesichtigung, FRAGE_AN_DER_BESICHTIGUNG)
  .refine((e) => e.nachtragId === undefined, OHNE_NACHTRAG);

/** Eine Antwort auf eine Frage an dieser Besichtigung. Kennung: die Antwort. */
const antwortEintrag = z
  .object({
    ...kopf,
    art: z.literal("antwort"),
    ...antwortFelder,
  })
  .refine((e) => e.nachtragId === undefined, OHNE_NACHTRAG);

/** „Erledigt“ – oder wieder „offen“. Kennung: die Frage. */
const frageStandEintrag = z
  .object({
    ...kopf,
    art: z.literal("frage-stand"),
    stand: z.enum(FRAGE_STAENDE),
  })
  .refine((e) => e.nachtragId === undefined, OHNE_NACHTRAG);

/** Der Montagetermin, mit dem Kunden vereinbart (Tag und Text, beides null: keiner). Kennung: die Anlage, wie bei K2. */
const montageterminEintrag = z
  .object({
    ...kopf,
    art: z.literal("montagetermin"),
    ...montageterminFelder,
  })
  .refine((e) => e.nachtragId === undefined, OHNE_NACHTRAG);

/**
 * Seit E3c (S6; PROJEKT.md, O97): Eine Besichtigung, die NIE gesendet wurde,
 * verwerfen – am Gerät und am Server. Kennung: die Aufnahme. Der Server
 * löscht sie samt ihrer Anlage (mit Kunde und Objekt, wenn nichts anderes
 * daran hängt) nach derselben Regel wie „Anlage löschen“ im CRM und setzt
 * einen Grabstein. Nur der, der sie aufgenommen hat, nur solange sie „in
 * Aufnahme“ ist (sonst 409 „abgeschlossen“). Zweimal – oder nie angekommen
 * – ergibt 200: Das Ziel ist erreicht. Geht wie alles über den Postausgang,
 * also auch ohne Netz.
 */
const verwerfenEintrag = z
  .object({
    ...kopf,
    art: z.literal("verwerfen"),
  })
  .refine((e) => e.kennung === e.aufnahmeId, { message: "Die Kennung ist die der Aufnahme.", path: ["kennung"] })
  .refine((e) => e.nachtragId === undefined, OHNE_NACHTRAG);

/** Ein Eintrag – unterschieden nach `art`. */
export const eintragEingabe = z.discriminatedUnion("art", [
  aufnahmeEintrag,
  verwerfenEintrag,
  stationEintrag,
  notizEintrag,
  laengeEintrag,
  fotoEintrag,
  memoEintrag,
  materialEintrag,
  k2Eintrag,
  geloeschtEintrag,
  abgeschlossenEintrag,
  nachtragEintrag,
  nachtragAbgeschlossenEintrag,
  frageEintrag,
  antwortEintrag,
  frageStandEintrag,
  montageterminEintrag,
]);
export type Eintrag = z.infer<typeof eintragEingabe>;
/**
 * Ein Eintrag, wie er vom Gerät kommt, bevor der Vertrag ihn liest – etwa
 * ein Abschluss ohne die Liste `materialangabe` (alter Stand der App). Für
 * Tests, die genau das schicken.
 */
export type EintragVomGeraet = z.input<typeof eintragEingabe>;
export type EintragArt = Eintrag["art"];
/** Ein Eintrag einer bestimmten Art, z. B. `EintragVon<"laenge">`. */
export type EintragVon<A extends EintragArt> = Extract<Eintrag, { art: A }>;

// --- Die Antwort -------------------------------------------------------------------

/**
 * Warum der Server einen Eintrag nicht annimmt (Status 409):
 *  - eltern-fehlt: das, woran er hängt, ist noch nicht da – später erneut senden
 *  - datei-fehlt: die Datei (Foto, Memo, PDF) ist noch nicht hochgeladen
 *  - abgeschlossen: die Aufnahme ist gesendet; ändern geht nur über einen Nachtrag
 *  - kennung-vergeben: die Kennung gehört schon einem anderen Datensatz
 *  - konflikt: passt nicht zu dem, was schon am Server ist
 *  - unvollstaendig: beim Abschluss fehlt noch etwas (`fehlt` sagt, was)
 *  - geloescht (seit E3c, S6): Das Büro hat diese Besichtigung gelöscht –
 *    sie kommt nicht wieder, auch nicht über „aufnahme“ (Grabstein, Prüfung
 *    des Plans E3c, Befund 1). Die App räumt ihre Kopie samt Postausgang weg.
 *    Werte kommen nur dazu, nie weg: Ein Gerät mit altem Stand kennt den
 *    Grund nicht und zeigt einen Dauerfehler – hinnehmbar (Befund 15).
 *  - buero-arbeitet (seit der Code-Prüfung E3c, Befund 3): nur auf
 *    „verwerfen“ – das Büro arbeitet schon mit der Anlage dieser
 *    Besichtigung (Montagetermin, K2, Bestellung …). Am Server bleibt sie;
 *    löschen kann sie nur die Leitung im CRM. Am Gerät ist sie schon weg, die
 *    App sagt es einmal.
 */
export const EINTRAG_ABWEISUNGEN = [
  "eltern-fehlt",
  "datei-fehlt",
  "abgeschlossen",
  "kennung-vergeben",
  "konflikt",
  "unvollstaendig",
  "geloescht",
  "buero-arbeitet",
] as const;
export type EintragAbweisung = (typeof EINTRAG_ABWEISUNGEN)[number];

/**
 * Die Antwort auf POST /api/doku/eintrag. 200 heißt: genau einmal da. Bei
 * 400 (ungültig) fehlt der Grund; 401 und 403 kommen vom Wächter
 * (`Abweisung`). Seit E2, Schritt 5 nimmt der Server alle Arten an – die
 * 501 („kommt in einem späteren Schritt“) gibt es nicht mehr.
 *
 * Bei „unvollstaendig“ (Abschluss, seit E2, Schritt 4) sagt der Server
 * genau, was nicht stimmt: `fehlt` – am Gerät gemeldet, am Server nicht da
 * (die App schickt es neu); `zuViel` – am Server da, am Gerät nicht mehr
 * (die App schickt „geloescht“). Nur Kennungen, nie Inhalte.
 */
export type EintragAntwort =
  | { ok: true }
  | { ok: false; grund?: EintragAbweisung; meldung: string; fehlt?: Partial<KennungsListen>; zuViel?: Partial<KennungsListen> };

/**
 * Die Antwort auf PUT /api/doku/datei/:pruefsumme. 200 heißt: Die Datei
 * liegt am Server (auch, wenn sie schon da war). Sonst: 400 (Prüfsumme
 * passt nicht, Inhalt nicht wie angegeben, Foto mit Metadaten), 413 (zu
 * groß), 415 (diese Art nicht), 507 (Platte fast voll – später erneut).
 */
export type DateiAntwort = { ok: true } | { ok: false; meldung: string };

// --- Material draußen (seit E3b, O65) ---------------------------------------------

/** Eine Materialangabe, wie sie am Server liegt (Teil der Besichtigung). */
export interface MaterialAmServer {
  id: string;
  stationId: string | null;
  standardId: string | null;
  artikelnummer: string | null;
  bezeichnung: string;
  menge: number;
  einheit: string;
  nachtragId: string | null;
  geaendertImNachtrag: string | null;
}

/**
 * Was der Verkäufer draußen antippen kann – die aktiven Einträge der
 * Standardliste „zur Auswahl“ (Claudes Vorschlag 2, Plan E3b; „immer dabei“
 * kommt erst im Büro dazu). `menge` ist die Vorgabe, `gruppe` fasst Varianten
 * zu einer Kachel zusammen.
 *
 *   GET /api/doku/standardmaterial      DraussenMaterialAntwort
 */
export interface DraussenMaterialZeile {
  id: string;
  artikelnummer: string | null;
  bezeichnung: string;
  menge: number;
  einheit: string;
  gruppe: string | null;
}

/**
 * Die Antwort auf GET /api/doku/standardmaterial – nach Gruppe und Bezeichnung.
 *
 * Seit E3c (S5): dazu die Adresse von K2 der Firma (Einstellung der Leitung,
 * ablauf-vertrag.ts) – für „Dachplanung starten“. Die App legt sie mit der
 * Liste am Gerät ab, damit der Knopf auch ohne Netz da ist. null: keine
 * eingetragen; ein Server mit altem Stand schickt das Feld nicht.
 */
export interface DraussenMaterialAntwort {
  eintraege: DraussenMaterialZeile[];
  k2Adresse?: string | null;
}

// --- Nachsehen: was gesendet ist (E2, Schritt 4) ----------------------------------
//
// Der Verkäufer behält Einsicht in alles, was er gesendet hat (Gernot,
// 19.10.2026): Texte und Längen bleiben am iPhone, Fotos, Memos und das
// K2-PDF holt die App bei Bedarf vom Server. Fehlt die Kopie am Gerät
// (anderes Gerät, neu installiert), füllt der Server sie auf. Dieselben
// Lese-Wege nutzt E3 fürs Büro.
//
//   GET /api/doku/aufnahmen             die eigenen gesendeten Besichtigungen
//   GET /api/doku/aufnahme/:id          eine ganz – alles außer den Dateien
//   GET /api/doku/datei/:pruefsumme     eine Datei, die an einer eigenen hängt

/** Wo eine Besichtigung am Server steht (Spalte `aufnahme.stand`). */
export const AUFNAHME_STAENDE = ["in_aufnahme", "zu_pruefen", "freigegeben"] as const;
export type AufnahmeStandAmServer = (typeof AUFNAHME_STAENDE)[number];

/** Eine Zeile unter „Gesendet“ – nur, was die Liste zeigt. */
export interface GesendetZeile {
  id: string;
  kundeName: string;
  strasse: string;
  plz: string;
  ort: string;
  /** Nie „in_aufnahme“ – die Liste kennt nur Gesendetes. */
  stand: AufnahmeStandAmServer;
  /** ISO-Zeitpunkt, an dem das Büro „alles da“ bestätigt hat. */
  gesendetAm: string;
  /** Wie viele Nachträge schon im Büro sind (E2, Schritt 5). */
  nachtraege: number;
  /**
   * Wie viele Fragen an dieser Besichtigung offen sind (seit E3) – alle
   * offenen, wer auch immer gefragt hat. Daraus das Band „2 offene Fragen“
   * oben in der App (Gernots Entscheidung 2); die Fragen selbst kommen mit
   * der Besichtigung (`AufnahmeAmServer.fragen`).
   */
  offeneFragen: number;
}

/**
 * Die Antwort auf GET /api/doku/aufnahmen – neueste zuerst.
 *
 * `geloescht` (seit E3c, S6; PROJEKT.md, O53): die Kennungen der eigenen
 * Besichtigungen, die das Büro in den letzten 30 Tagen gelöscht hat –
 * auch solche, die nie gesendet waren („in Aufnahme“, Manuels Tipp-Kunde).
 * Die App räumt dazu sofort ihre Kopie samt Postausgang und Dateien weg und
 * sagt es einmal; „fehlt in der Liste“ mit der Drei-Tage-Frist bleibt als
 * Rückfall. Ein Server mit altem Stand schickt das Feld nicht.
 */
export interface GesendetAntwort {
  aufnahmen: GesendetZeile[];
  geloescht?: string[];
}

/**
 * Eine Besichtigung, wie sie am Server liegt – die Antwort auf GET
 * /api/doku/aufnahme/:id. Ohne Dateien: Fotos, Memos und das K2-PDF
 * tragen nur ihre Prüfsumme (die Datei gibt es über den Datei-Weg).
 * Zeitpunkte als ISO-Text, Meter und Dauer als Zahl.
 */
export interface AufnahmeAmServer {
  id: string;
  stand: AufnahmeStandAmServer;
  begonnenAm: string;
  gesendetAm: string | null;
  /** `vorname`, `nachname` seit E3b (O82) – null: (noch) nicht aufgeteilt; ein Server mit altem Stand schickt sie nicht. */
  kunde: { id: string; art: "person"; name: string; telefon: string | null; email: string | null; vorname?: string | null; nachname?: string | null };
  objekt: { id: string; art: "einfamilienhaus"; strasse: string; plz: string; ort: string };
  anlage: {
    id: string;
    produktlinie: "sun";
    /**
     * Die Dachplanung mit K2: das PDF (Prüfsumme, Größe in Bytes, wenn bekannt)
     * oder übersprungen. Seit E3c (S5) dazu der Excel-Report (`excel`, null:
     * keiner) – ein Server mit altem Stand schickt ihn nicht.
     */
    k2: { pruefsumme: string | null; groesse: number | null; uebersprungen: boolean; excel?: { pruefsumme: string; groesse: number | null } | null };
    /**
     * Der Montagetermin, mit dem Kunden vereinbart (seit E3, Gernots
     * Entscheidung 4): der Tag „JJJJ-MM-TT“ und ein Text („Partie 2, 3 Tage“) –
     * beides null: noch keiner.
     */
    montagetermin: { am: string | null; text: string | null };
  };
  stationen: {
    id: string;
    art: string;
    nr: number;
    fertig: boolean;
    sonstiges: string | null;
    geaendertImNachtrag: string | null;
    /**
     * Seit E3c (S5): die Gegebenheiten am Dach (dach-fakten.ts) – nur an der
     * Station „dach“, null: nichts erfasst. Ein Server mit altem Stand
     * schickt das Feld nicht.
     */
    fachfragen?: DachFakten | null;
  }[];
  fotos: {
    id: string;
    stationId: string;
    reihenfolge: number;
    pruefsumme: string;
    breite: number;
    hoehe: number;
    groesse: number;
    aufnahmezeit: string | null;
    zeichnung: Zeichnung | null;
    nachtragId: string | null;
  }[];
  notizen: { id: string; stationId: string | null; fotoId: string | null; text: string; nachtragId: string | null }[];
  memos: {
    id: string;
    notizId: string;
    /** Das Original – bis E3 zugleich die Fassung zum Abspielen. */
    pruefsumme: string;
    format: MemoFormat;
    dauerSekunden: number;
    aufgenommenAm: string;
    nachtragId: string | null;
  }[];
  laengen: {
    id: string;
    stationId: string;
    laengenArt: string;
    meter: number;
    anmerkung: string | null;
    nachtragId: string | null;
    geaendertImNachtrag: string | null;
  }[];
  /**
   * Das Material von draußen (seit E3b, O65) – nach Station (die Seite
   * „Material“ zuletzt), darin wie angelegt. `stationId` null: auf der Seite
   * „Material“; `standardId` null: „Etwas anderes“. Ein Server mit altem
   * Stand schickt die Liste nicht – die App liest dann „keines“.
   */
  material: MaterialAmServer[];
  /** „Etwas nachreichen“ (E2, Schritt 5) – ältester zuerst; `gesendetAm` null: noch offen. */
  nachtraege: { id: string; begonnenAm: string; gesendetAm: string | null }[];
  /**
   * Die Fragen an dieser Besichtigung, älteste zuerst, je mit ihren Antworten
   * (seit E3, Grundsatz 9) – offene und erledigte. Woran eine Frage hängt,
   * sagt ihr Bezug (Anlage, Besichtigung, Station, Foto, Notiz).
   */
  fragen: FrageAmServer[];
}
